export const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 100);
};

/**
 * Normalizes a URL by removing protocol and trailing slashes
 * @param url - The URL to normalize
 * @returns The normalized URL
 */
export const normalizeUrl = (url: string): string => {
  if (!url) return url;
  
  return url
    .replace(/^https?:\/\//i, '') // Remove http:// or https://
    .replace(/\/+$/, ''); // Remove trailing slashes
};

/**
 * Ensures a URL has a protocol for external links
 * @param url - The URL to format
 * @returns The URL with protocol
 */
export const ensureProtocol = (url: string): string => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://${url}`;
};

export const generateTaskUrl = (brandSlug: string, taskSlug: string): string => {
  return `/task/${brandSlug}/${taskSlug}`;
};

export const generateTechnicalTaskUrl = (brandId: string, taskId: string): string => {
  return `/tasks/${brandId}/${taskId}`;
};

export const generateBlogPostUrl = (slug: string): string => {
  return `/blogposts/${slug}`;
};

export const copyRichContentToClipboard = async (content: string): Promise<boolean> => {
  try {
    // Create a temporary div to convert markdown-like content to HTML
    const tempDiv = document.createElement('div');
    
    // Basic markdown to HTML conversion
    let htmlContent = content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/## (.*)/g, '<h2>$1</h2>')
      .replace(/# (.*)/g, '<h1>$1</h1>')
      .replace(/^- (.*)/gm, '<li>$1</li>')
      .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/^(?!<[h|u|l])/gm, '<p>')
      .replace(/(?<!>)$/gm, '</p>');
    
    tempDiv.innerHTML = htmlContent;
    
    // Copy both HTML and plain text
    const clipboardItem = new ClipboardItem({
      'text/html': new Blob([tempDiv.innerHTML], { type: 'text/html' }),
      'text/plain': new Blob([content], { type: 'text/plain' })
    });
    
    await navigator.clipboard.write([clipboardItem]);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    // Fallback to plain text copy
    try {
      await navigator.clipboard.writeText(content);
      return true;
    } catch (fallbackError) {
      console.error('Fallback copy also failed:', fallbackError);
      return false;
    }
  }
};

/**
 * URL prefixes configuration for CMS content types
 */
export interface CMSUrlPrefixes {
  article?: string;
  articles?: string;
  author?: string;
  authors?: string;
  tag?: string;
  tags?: string;
  category?: string;
  categories?: string;
}

/**
 * Generates a preview URL for CMS content using configured prefixes
 * @param siteUrl - The base site URL
 * @param prefixes - The URL prefixes configuration
 * @param type - The content type (article, author, tag, category)
 * @param slug - The content slug
 * @returns The full preview URL or null if required params are missing
 */
export const generateCMSPreviewUrl = (
  siteUrl: string | null,
  prefixes: CMSUrlPrefixes | null,
  type: keyof CMSUrlPrefixes,
  slug: string
): string | null => {
  if (!siteUrl || !slug) return null;
  
  const prefix = prefixes?.[type] || type;
  const baseUrl = siteUrl.replace(/\/$/, ''); // Remove trailing slash
  const cleanPrefix = prefix.replace(/^\/|\/$/g, ''); // Remove leading/trailing slashes
  const cleanSlug = slug.replace(/^\/|\/$/g, ''); // Remove leading/trailing slashes from slug
  
  return `${baseUrl}/${cleanPrefix}/${cleanSlug}`;
};

/** Events: build list URL from settings */
export const buildEventsListUrl = (settings: { site_url?: string | null; url_prefixes?: { events?: string } | null } | null): string | null => {
  if (!settings?.site_url) return null;
  const base = settings.site_url.replace(/\/$/, '');
  const prefix = (settings.url_prefixes?.events || 'events').replace(/^\/|\/$/g, '');
  return `${base}/${prefix}`;
};

/** Events: build single event URL from settings and slug */
export const buildEventDetailUrl = (
  settings: { site_url?: string | null; url_prefixes?: { event?: string } | null } | null,
  slug: string
): string | null => {
  if (!settings?.site_url || !slug) return null;
  const base = settings.site_url.replace(/\/$/, '');
  const prefix = (settings.url_prefixes?.event || 'events').replace(/^\/|\/$/g, '');
  const cleanSlug = slug.replace(/^\/|\/$/g, '');
  return `${base}/${prefix}/${cleanSlug}`;
};

/** Podcast: build episodes list URL from settings */
export const buildPodcastEpisodesListUrl = (settings: { site_url?: string | null; url_prefixes?: { episodes?: string } | null } | null): string | null => {
  if (!settings?.site_url) return null;
  const base = settings.site_url.replace(/\/$/, '');
  const prefix = (settings.url_prefixes?.episodes || 'podcast').replace(/^\/|\/$/g, '');
  return `${base}/${prefix}`;
};

/** Podcast: build single episode URL from settings and slug */
export const buildPodcastEpisodeDetailUrl = (
  settings: { site_url?: string | null; url_prefixes?: { episode?: string } | null } | null,
  slug: string
): string | null => {
  if (!settings?.site_url || !slug) return null;
  const base = settings.site_url.replace(/\/$/, '');
  const prefix = (settings.url_prefixes?.episode || 'podcast').replace(/^\/|\/$/g, '');
  const cleanSlug = slug.replace(/^\/|\/$/g, '');
  return `${base}/${prefix}/${cleanSlug}`;
};